import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { buildBreadcrumbScript } from "@/lib/serviceHubSchema";

const SITE_URL = "https://coloradowebimpressions.com";
const LAST_UPDATED = "April 26, 2026";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy | Colorado Web Impressions" },
      {
        name: "description",
        content:
          "How Colorado Web Impressions collects, uses, and protects your information. Plain-English privacy policy for our Colorado Springs digital marketing agency.",
      },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: "Privacy Policy | Colorado Web Impressions" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/privacy` }],
    scripts: [
      buildBreadcrumbScript([
        { name: "Home", path: "/" },
        { name: "Privacy Policy", path: "/privacy" },
      ]),
    ],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <SiteLayout>
      <PageHero
        eyebrow="Legal"
        title="Privacy Policy"
        description={`Last updated: ${LAST_UPDATED}. We keep this short, plain, and honest — the same way we run the rest of the business.`}
      />

      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <article className="prose-article">
            <h2>Who we are</h2>
            <p>
              Heidlebaugh Digital LLC (dba Colorado Web Impressions;
              &ldquo;we,&rdquo; &ldquo;us,&rdquo; &ldquo;our&rdquo;) is a
              digital marketing agency based in Colorado Springs, Colorado.
              You can reach us at{" "}
              <a href="mailto:info@coloradowebimpressions.com" aria-label="Email Colorado Web Impressions">
                info@coloradowebimpressions.com
              </a>{" "}
              or <a href="tel:+17194193935" aria-label="Call (719) 419-3935">(719) 419-3935</a>.
            </p>
            <p>
              <strong>Legal entity:</strong> Heidlebaugh Digital LLC dba Colorado
              Web Impressions, 3210 E Woodmen Rd Ste 110, Colorado Springs, CO 80920.
            </p>

            <h2>What we collect</h2>
            <p>We collect only what we need to do business with you:</p>
            <ul>
              <li>
                <strong>Contact form & call info:</strong> name, email, phone,
                business name, and the message you send us.
              </li>
              <li>
                <strong>Website analytics:</strong> aggregated, anonymized data
                like page views, device type, and referring source. We use this
                only to improve the site.
              </li>
              <li>
                <strong>Cookies:</strong> minimal first-party cookies for site
                functionality. We do not sell or share cookie data.
              </li>
            </ul>

            <h2>How we use it</h2>
            <ul>
              <li>To respond to your inquiry or proposal request.</li>
              <li>To deliver services you&apos;ve hired us for.</li>
              <li>
                To send occasional emails about your project. We do not send
                spam, mass marketing blasts, or sell your info to anyone — ever.
              </li>
            </ul>

            <h2>Who we share it with</h2>
            <p>
              We share information only with the small set of vendors required
              to run the business (e.g., email provider, hosting, analytics).
              We never sell your data. We never trade or rent contact lists.
            </p>

            <h2>How long we keep it</h2>
            <p>
              We retain client information for the duration of our engagement
              plus 7 years for tax and legal purposes. Inquiry data from people
              who never become clients is deleted within 24 months.
            </p>

            <h2>Text messaging (SMS)</h2>
            <p>
              If you opt in to SMS by checking the consent box on our contact
              or proposal forms, we use your phone number only to text you
              about your inquiry, appointments, or active project. Message
              frequency varies. Message and data rates may apply. Reply{" "}
              <strong>STOP</strong> to opt out or <strong>HELP</strong> for
              help. We never sell, rent, or share phone numbers or SMS opt-in
              data with third parties for marketing. See our{" "}
              <Link
                to="/sms-policy"
                className="font-bold text-brand-gold-deep underline"
              >
                SMS Policy
              </Link>{" "}
              for the full disclosure.
            </p>

            <h2>Your rights</h2>
            <p>
              You can request a copy of the information we hold about you, ask
              us to correct it, or ask us to delete it. Email{" "}
              <a href="mailto:info@coloradowebimpressions.com" aria-label="Email Colorado Web Impressions">
                info@coloradowebimpressions.com
              </a>{" "}
              and we&apos;ll respond within 30 days.
            </p>

            <h2>Children</h2>
            <p>
              Our services are for businesses, not children. We do not
              knowingly collect information from anyone under 16.
            </p>

            <h2>Changes to this policy</h2>
            <p>
              When we update this policy, we&apos;ll change the &ldquo;Last
              updated&rdquo; date at the top. Material changes will be
              highlighted on this page for at least 30 days.
            </p>
          </article>
        </div>
      </section>
    </SiteLayout>
  );
}
